import { Component, computed, inject, OnInit } from '@angular/core';

import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDialogModule } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatSlideToggleChange, MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';
import { REMINDER_TIME_OPTIONS, REMINDER_TIMES_MAX } from '@equip-track/shared';
import { LanguageService } from '../../services/language.service';
import { ThemeService } from '../../services/theme.service';
import { UserPreferencesService } from '../../services/user-preferences.service';
import { NotificationSettingsStore } from '../../store/notification-settings.store';

/** GitHub issue tracking the Telegram notification channel (next phase). */
export const TELEGRAM_NOTIFICATIONS_ISSUE_URL =
  'https://github.com/NetanelAlbert/EquipTrack/issues/329';

@Component({
  selector: 'app-settings',
  standalone: true,
  imports: [
    MatDialogModule,
    MatDividerModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    MatButtonModule,
    MatTooltipModule,
    MatSlideToggleModule,
    MatCheckboxModule,
    TranslateModule,
  ],
  templateUrl: './settings.component.html',
  styleUrl: './settings.component.scss',
})
export class SettingsComponent implements OnInit {
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly userPreferencesService = inject(UserPreferencesService);
  readonly notificationSettingsStore = inject(NotificationSettingsStore);

  readonly selectedLanguage = this.languageService.language;
  readonly availableLanguages = this.languageService.availableLanguages;

  readonly selectedInventoryView = this.userPreferencesService.inventoryView;
  readonly availableInventoryViews = this.userPreferencesService.availableInventoryViews;

  /** @deprecated */
  readonly selectedFormItemsView = this.selectedInventoryView;
  /** @deprecated */
  readonly availableFormItemsViews = this.availableInventoryViews;

  readonly selectedMode = this.themeService.mode;
  readonly selectedPalette = this.themeService.palette;
  readonly availableModes = this.themeService.availableModes;
  readonly availablePalettes = this.themeService.availablePalettes;

  readonly currentModeOption = computed(() =>
    this.availableModes.find((m) => m.value === this.selectedMode())
  );
  readonly currentPaletteOption = computed(() =>
    this.availablePalettes.find((p) => p.value === this.selectedPalette())
  );

  readonly telegramIssueUrl = TELEGRAM_NOTIFICATIONS_ISSUE_URL;
  readonly reminderTimeOptions = REMINDER_TIME_OPTIONS;
  readonly reminderTimesMax = REMINDER_TIMES_MAX;

  ngOnInit(): void {
    void this.notificationSettingsStore.load();
  }

  onLanguageChange(event: MatSelectChange): void {
    if (this.languageService.isSupportedLanguage(event.value)) {
      this.languageService.setLanguage(event.value);
      void this.notificationSettingsStore.syncLanguageIfEnabled();
    }
  }

  onInventoryViewChange(event: MatSelectChange): void {
    if (this.userPreferencesService.isSupportedInventoryView(event.value)) {
      this.userPreferencesService.setInventoryView(event.value);
    }
  }

  /** @deprecated */
  onFormItemsViewChange(event: MatSelectChange): void {
    this.onInventoryViewChange(event);
  }

  onModeChange(event: MatSelectChange): void {
    if (this.themeService.isSupportedMode(event.value)) {
      this.themeService.setMode(event.value);
    }
  }

  onPaletteChange(event: MatSelectChange): void {
    if (this.themeService.isSupportedPalette(event.value)) {
      this.themeService.setPalette(event.value);
    }
  }

  onRemindersEnabledChange(event: MatSlideToggleChange): void {
    const enabled = event.checked;
    void this.notificationSettingsStore.savePreferences({
      enabled,
      reminderTimes: this.notificationSettingsStore.reminderTimes(),
      channels: {
        browser: enabled,
        telegram: false,
      },
      language: this.selectedLanguage(),
    });
  }

  onReminderTimeChange(index: number, event: MatSelectChange): void {
    void this.notificationSettingsStore.updateReminderTime(index, event.value);
  }

  onAddReminderTime(): void {
    void this.notificationSettingsStore.addReminderTime();
  }

  onRemoveReminderTime(index: number): void {
    void this.notificationSettingsStore.removeReminderTime(index);
  }

  onBrowserChannelChange(checked: boolean): void {
    void this.notificationSettingsStore.savePreferences({
      enabled: this.notificationSettingsStore.remindersEnabled(),
      reminderTimes: this.notificationSettingsStore.reminderTimes(),
      channels: {
        browser: checked,
        telegram: false,
      },
      language: this.selectedLanguage(),
    });
  }
}
